import { useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { useLiveQuery } from 'dexie-react-hooks';
import { AlertTriangle, ArrowLeftRight, Beaker, BookOpen, CloudOff, Home, Lock, Menu, MessageSquareWarning, Moon, RefreshCw, Smartphone, Sun, SunMoon, UserRound } from 'lucide-react';
import { FeedbackDialog } from '@/features/feedback/FeedbackDialog';
import { useSyncStatus } from '@/sync/engine';
import { brand } from '@/config/brand';
import { rt } from '@/data/runtime';
import { isManagerOrAbove, ROLE_LABEL } from '@/domain/permissions';
import { Badge, Button, Card, cn } from '@/ui/core';
import { ButtonLink } from '@/ui/ButtonLink';
import { Logo } from '@/ui/Logo';
import { Dialog } from '@/ui/overlay';
import { isManagerPath, MANAGER_BOTTOM_NAV, navFor, type NavItem } from './nav';
import { useSession, useTheme } from './providers';
import { useStorageError } from './hooks';
import { WhoDialog } from './WhoDialog';
import { useLeavePractice } from './LeavePractice';

export function AppLayout() {
  const { pathname } = useLocation();
  return isManagerPath(pathname) ? <ManagerShell /> : <StaffShell />;
}

function SkipLink() {
  return (
    <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded-lg focus:bg-surface focus:px-4 focus:py-2">
      Skip to content
    </a>
  );
}

/** Nights finished by staff and waiting for a manager. */
export function useWaitingNights(): number {
  return useLiveQuery(() => rt().db.nights.where('status').equals('finished').count(), [], 0);
}

// ---------------------------------------------------------------------------
// Staff screens: no menus, a Home button and the person's name.
// ---------------------------------------------------------------------------

function StaffShell() {
  const { pathname } = useLocation();
  const { actor } = useSession();
  const [whoOpen, setWhoOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const atHome = pathname === '/';
  return (
    <div className="min-h-dvh">
      <SkipLink />
      {/* Sized for a tablet first: taller bar, bigger logo and buttons from md up. */}
      <header className="sticky top-0 z-30 h-16 border-b-4 border-accent-fill bg-surface/95 backdrop-blur md:h-20 no-print">
        <div className="mx-auto flex h-full max-w-6xl items-center gap-1.5 px-3 sm:gap-2 sm:px-4 md:gap-4 md:px-6">
          {!atHome && (
            <Link to="/" className="flex min-h-12 shrink-0 items-center gap-1.5 rounded-xl border-2 border-border px-2.5 text-base font-bold hover:bg-surface-2 sm:gap-2 sm:px-3 md:min-h-14 md:px-4 md:text-lg">
              <Home className="size-6" aria-hidden /> Home
            </Link>
          )}
          {/* On a phone the Home button already leads home, so the logo makes room for it. */}
          <Link to="/" className={cn('shrink-0 items-center rounded-lg', atHome ? 'flex' : 'hidden sm:flex')} aria-label="Home">
            <Logo className="h-11 md:h-14" />
          </Link>
          <div className="ml-auto flex min-w-0 items-center gap-1.5 md:gap-2">
            <ModeBadge />
            <SyncBadge quiet />
            <Link to="/help" className="flex min-h-12 items-center gap-2 rounded-xl border-2 border-border px-3 text-base font-semibold hover:bg-surface-2 md:min-h-14 md:px-4 md:text-lg" aria-label="How to use the app">
              <BookOpen className="size-5 md:size-6" aria-hidden />
              <span className="hidden sm:inline">Help</span>
            </Link>
            {/* On a tablet, from any screen (it notes which one). On a phone it's on the home screen. */}
            <button type="button" onClick={() => setFeedbackOpen(true)} className="hidden min-h-14 items-center gap-2 rounded-xl border-2 border-border px-3 text-lg font-semibold hover:bg-surface-2 md:flex md:px-4" aria-label="Report a problem or an idea" title="Report a problem or an idea">
              <MessageSquareWarning className="size-6" aria-hidden />
              <span className="hidden lg:inline">Feedback</span>
            </button>
            <button type="button" onClick={() => setWhoOpen(true)} className="flex min-h-12 items-center gap-2 rounded-xl border-2 border-border px-3 hover:bg-surface-2 md:min-h-14 md:px-4" aria-label={`${actor.name} is using the tablet. Change person`}>
              <UserRound className="size-5 md:size-6" aria-hidden />
              <span className="max-w-20 truncate text-base font-semibold sm:max-w-28 md:max-w-40 md:text-lg">{actor.name.split(' ')[0]}</span>
            </button>
          </div>
        </div>
        <WhoDialog open={whoOpen} onClose={() => setWhoOpen(false)} />
        <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
      </header>
      <main id="main" className="mx-auto max-w-6xl px-4 pb-10 pt-4 sm:px-6 md:pt-6" key={pathname.split('/')[1]}>
        <Banners />
        <Outlet />
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manager area: full navigation.
// ---------------------------------------------------------------------------

function ManagerShell() {
  const { actor } = useSession();
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const allowed = isManagerOrAbove(actor.role);
  const groups = navFor(actor.role);
  const waiting = useWaitingNights();
  const badges: Badges = { nights: allowed ? waiting : 0 };
  return (
    <div className="min-h-dvh">
      <SkipLink />
      <ManagerTopBar onMenu={() => setMenuOpen(true)} showMenu={allowed} />
      <div className="mx-auto flex max-w-[90rem]">
        {allowed && (
          <aside className="sticky top-16 hidden h-[calc(100dvh-4rem)] w-64 shrink-0 overflow-y-auto border-r border-border px-3 py-4 lg:block no-print" aria-label="Manager navigation">
            <NavGroups groups={groups} badges={badges} />
          </aside>
        )}
        <main id="main" className={cn('min-w-0 flex-1 px-4 pt-4 sm:px-6 lg:pb-10', allowed ? 'pb-24' : 'pb-10')} key={pathname.split('/')[2] ?? ''}>
          <Banners />
          {allowed ? <Outlet /> : <ManagersOnly />}
        </main>
      </div>
      {allowed && (
        <nav aria-label="Manager quick navigation" className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-surface/95 backdrop-blur lg:hidden no-print" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          <ul className="grid grid-cols-5">
            {MANAGER_BOTTOM_NAV.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} end={item.end} className={({ isActive }) => cn('relative flex min-h-16 flex-col items-center justify-center gap-1 text-xs font-semibold', isActive ? 'text-accent' : 'text-muted')}>
                  <item.icon className="size-6" aria-hidden />
                  {item.label}
                  {item.badge && badges[item.badge] > 0 && <CountBadge n={badges[item.badge]} className="absolute right-[calc(50%-1.6rem)] top-1.5" />}
                </NavLink>
              </li>
            ))}
            <li>
              <button type="button" onClick={() => setMenuOpen(true)} className="flex min-h-16 w-full flex-col items-center justify-center gap-1 text-xs font-semibold text-muted">
                <Menu className="size-6" aria-hidden />
                Menu
              </button>
            </li>
          </ul>
        </nav>
      )}
      <Dialog open={menuOpen} onClose={() => setMenuOpen(false)} title="Manager menu">
        <div onClick={(e) => (e.target as HTMLElement).closest('a') && setMenuOpen(false)}>
          <ButtonLink to="/" block size="lg" className="mb-5" icon={<ArrowLeftRight className="size-5" aria-hidden />}>
            Back to the staff screen
          </ButtonLink>
          <NavGroups groups={groups} badges={badges} large />
        </div>
      </Dialog>
    </div>
  );
}

function ManagersOnly() {
  const [whoOpen, setWhoOpen] = useState(false);
  return (
    <div className="mx-auto max-w-lg">
      <Card className="flex flex-col items-center gap-4 py-8 text-center">
        <span className="flex size-16 items-center justify-center rounded-full bg-surface-2">
          <Lock className="size-8 text-muted" aria-hidden />
        </span>
        <h1 className="text-2xl font-bold">This part is for managers</h1>
        <p className="text-lg text-muted">Everything you need for your shift is on the staff screen.</p>
        <ButtonLink to="/" variant="primary" size="xl" block icon={<Home className="size-6" aria-hidden />}>
          Go to the staff screen
        </ButtonLink>
        <Button size="lg" block onClick={() => setWhoOpen(true)}>
          I’m a manager — change person
        </Button>
      </Card>
      <WhoDialog open={whoOpen} onClose={() => setWhoOpen(false)} />
    </div>
  );
}

type Badges = Record<NonNullable<NavItem['badge']>, number>;

/** A small number bubble: things waiting for a manager. */
export function CountBadge({ n, className }: { n: number; className?: string }) {
  return (
    <span className={cn('inline-flex min-w-6 items-center justify-center rounded-full bg-warn px-1.5 text-xs font-bold leading-6 text-white dark:text-black', className)} aria-label={`${n} waiting for you`}>
      {n}
    </span>
  );
}

function NavGroups({ groups, large, badges }: { groups: ReturnType<typeof navFor>; large?: boolean; badges: Badges }) {
  return (
    <div className="flex flex-col gap-5">
      {groups.map((g) => (
        <div key={g.label}>
          <p className="mb-1 px-3 text-xs font-bold uppercase tracking-wider text-muted">{g.label}</p>
          <ul className="flex flex-col gap-0.5">
            {g.items.map((item) => (
              <li key={item.to}>
                <NavLink to={item.to} end={item.end} className={({ isActive }) => cn('flex items-center gap-3 rounded-xl px-3 font-medium', large ? 'min-h-13 text-base' : 'min-h-11 text-sm', isActive ? 'bg-accent-soft text-text' : 'text-text/85 hover:bg-surface-2')}>
                  <item.icon className="size-5 shrink-0 text-muted" aria-hidden />
                  <span className="flex-1">{item.label}</span>
                  {item.badge && badges[item.badge] > 0 && <CountBadge n={badges[item.badge]} />}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function ManagerTopBar({ onMenu, showMenu }: { onMenu: () => void; showMenu: boolean }) {
  const { actor, settings } = useSession();
  const [whoOpen, setWhoOpen] = useState(false);
  return (
    <header className="sticky top-0 z-30 h-16 border-b-4 border-accent-fill bg-surface/95 backdrop-blur no-print">
      <div className="mx-auto flex h-full max-w-[90rem] items-center gap-2 px-3 sm:px-4">
        {showMenu && (
          <button type="button" onClick={onMenu} className="hidden size-11 items-center justify-center rounded-xl hover:bg-surface-2 sm:flex lg:hidden" aria-label="Open manager menu">
            <Menu className="size-6" aria-hidden />
          </button>
        )}
        <NavLink to="/admin" className="flex min-w-0 flex-1 items-center gap-2">
          <Logo className="h-10 shrink-0" />
          <span className="hidden min-w-0 border-l border-border pl-2 lg:block">
            <span className="block truncate text-sm font-bold leading-tight">Manager area</span>
            <span className="block truncate text-xs text-muted">{settings.venueName}</span>
          </span>
        </NavLink>
        <div className="flex shrink-0 items-center gap-1.5">
          <ModeBadge />
          <SyncBadge />
          <ThemeToggle />
          <Link to="/help" className="flex size-11 items-center justify-center rounded-xl hover:bg-surface-2" aria-label="How to use the app" title="How to use the app">
            <BookOpen className="size-5" aria-hidden />
          </Link>
          <span className="hidden md:block">
            <ButtonLink to="/" size="sm" icon={<ArrowLeftRight className="size-4" aria-hidden />}>
              Staff screen
            </ButtonLink>
          </span>
          <button type="button" onClick={() => setWhoOpen(true)} className="flex min-h-11 items-center gap-2 rounded-xl border border-border px-2.5 hover:bg-surface-2" aria-label={`${actor.name}, ${ROLE_LABEL[actor.role]}. Change person`}>
            <UserRound className="size-5" aria-hidden />
            <span className="hidden max-w-32 truncate text-sm font-semibold md:inline">{actor.name.split(' ')[0]}</span>
            <span className="hidden lg:inline">
              <Badge tone={actor.role === 'staff' ? 'neutral' : 'accent'}>{ROLE_LABEL[actor.role]}</Badge>
            </span>
          </button>
        </div>
      </div>
      <WhoDialog open={whoOpen} onClose={() => setWhoOpen(false)} />
    </header>
  );
}

/** "Practice" at the top of every screen; tapping it offers the way back to the real counts. */
function ModeBadge() {
  const { mode } = useSession();
  const leave = useLeavePractice();
  if (mode !== 'practice') return null;
  return (
    <button type="button" onClick={() => void leave()} className="flex min-h-11 items-center rounded-xl" aria-label="Practice mode. Go to the real counts" title="Made-up numbers, kept apart from the real counts. Tap to go to the real counts.">
      <Badge tone="warn">
        <Beaker className="size-3.5" aria-hidden /> Practice
      </Badge>
    </button>
  );
}

/**
 * Sharing status. Staff screens (`quiet`) only show it when something needs attention; the
 * manager area always shows it while sharing is on.
 */
function SyncBadge({ quiet }: { quiet?: boolean }) {
  const s = useSyncStatus();
  if (s.state === 'off') return null;
  if (s.state === 'offline')
    return (
      <Badge tone="warn" title="Saved on this device. It goes to the other devices when the internet is back.">
        <CloudOff className="size-3.5" aria-hidden /> <span className="hidden sm:inline">Not shared yet</span>
      </Badge>
    );
  if (s.state === 'error')
    return (
      <Link to="/admin/data" title={s.message ?? undefined}>
        <Badge tone="danger">
          <AlertTriangle className="size-3.5" aria-hidden /> <span className="hidden sm:inline">Sharing problem</span>
        </Badge>
      </Link>
    );
  if (quiet) return null;
  return (
    <Badge tone={s.pending > 0 ? 'info' : 'ok'} title={s.pending > 0 ? `${s.pending} changes waiting to go` : 'Up to date with the other devices'}>
      <Smartphone className="size-3.5" aria-hidden /> <span className="hidden sm:inline">{s.pending > 0 ? 'Sending…' : 'Shared'}</span>
    </Badge>
  );
}

function ThemeToggle() {
  const { pref, setPref } = useTheme();
  const next = pref === 'system' ? 'dark' : pref === 'dark' ? 'light' : 'system';
  const Icon = pref === 'dark' ? Moon : pref === 'light' ? Sun : SunMoon;
  return (
    <button type="button" onClick={() => setPref(next)} className="hidden size-11 items-center justify-center rounded-xl hover:bg-surface-2 sm:flex" aria-label={`Screen colours: ${pref}. Switch to ${next}`} title={`Screen colours: ${pref}`}>
      <Icon className="size-5" aria-hidden />
    </button>
  );
}

function Banners() {
  const [storageErr, clearStorageErr] = useStorageError();
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW();
  const items: ReactNode[] = [];
  if (storageErr)
    items.push(
      <div key="storage" role="alert" className="flex items-start gap-3 rounded-xl border border-danger/50 bg-danger-soft p-3 text-sm">
        <AlertTriangle className="mt-0.5 size-5 shrink-0 text-danger" aria-hidden />
        <div className="flex-1">
          <p className="font-semibold text-danger">Storage problem — the last change was not saved</p>
          <p>{storageErr.message}</p>
        </div>
        <Button size="sm" onClick={clearStorageErr}>
          Dismiss
        </Button>
      </div>,
    );
  if (needRefresh)
    items.push(
      <div key="update" role="status" className="flex items-center gap-3 rounded-xl border border-info/50 bg-info-soft p-3 text-sm">
        <RefreshCw className="size-5 shrink-0 text-info" aria-hidden />
        <p className="flex-1">A new version of {brand.name} is ready. Nothing you saved changes.</p>
        <Button size="sm" variant="primary" onClick={() => updateServiceWorker(true)}>
          Update now
        </Button>
      </div>,
    );
  if (items.length === 0) return null;
  return <div className="mb-4 flex flex-col gap-2 no-print">{items}</div>;
}
